/** Same-origin admin requests use the server session cookie and an in-memory CSRF token. */
const TOKEN_KEY = "evalyte.admin.token";
try {
  localStorage.removeItem(TOKEN_KEY);
} catch {
  /* Storage may be unavailable. */
}
let csrfToken: string | null = null;
let onUnauthorised: (() => void) | null = null;
export function setCsrfToken(value: string | null) {
  csrfToken = value;
}
export function setUnauthorisedHandler(handler: () => void) {
  onUnauthorised = handler;
}
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
    public retryAfter?: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}
interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  signal?: AbortSignal;
}
function buildUrl(path: string, query?: RequestOptions["query"]) {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query))
    if (value !== null && value !== undefined && value !== "")
      params.set(key, String(value));
  return params.size ? `${path}?${params}` : path;
}
export async function request<T>(
  path: string,
  { method = "GET", body, query, signal }: RequestOptions = {},
): Promise<T> {
  const response = await fetch(buildUrl(path, query), {
    method,
    signal,
    credentials: "same-origin",
    headers: {
      ...(body === undefined ? {} : { "Content-Type": "application/json" }),
      ...(csrfToken &&
      !["GET", "HEAD", "OPTIONS"].includes(method.toUpperCase())
        ? { "X-Admin-CSRF": csrfToken }
        : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) {
    let message = String(response.status);
    let code: string | undefined;
    try {
      const payload: unknown = await response.json();
      if (payload && typeof payload === "object" && "detail" in payload) {
        const detail = payload.detail;
        if (typeof detail === "string") message = detail;
        else if (detail && typeof detail === "object") {
          if ("message" in detail && typeof detail.message === "string")
            message = detail.message;
          if ("code" in detail && typeof detail.code === "string")
            code = detail.code;
        }
      }
    } catch {
      /* HTTP status remains available. */
    }
    if (
      response.status === 401 &&
      (code === "ADMIN_SESSION_REQUIRED" || code === "ADMIN_SESSION_EXPIRED")
    ) {
      setCsrfToken(null);
      onUnauthorised?.();
    }
    const retry = Number(response.headers.get("Retry-After"));
    throw new ApiError(
      response.status,
      message,
      code,
      Number.isFinite(retry) && retry > 0 ? retry : undefined,
    );
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}
export interface AccountSummary {
  id: string;
  email: string;
  display_name: string;
  is_super_admin: boolean;
}
export interface Session {
  user: AccountSummary;
  csrf_token: string;
  expires_at: string;
}
export interface Setup {
  setup_token: string;
  secret: string;
  otpauth_uri: string;
  qr_svg_data_url: string;
  expires_at: string;
}
export interface RecoverySetup extends Setup {
  recovery_setup_required: true;
}
export interface ConfirmedSession extends Session {
  recovery_codes: string[];
}
const BASE = "/api/v1/admin/auth";
export const api = {
  me: () => request<Session & { mfa_enabled: true }>(`${BASE}/me`),
  login: (email: string, password: string) =>
    request<{ challenge_token: string; expires_at: string; method: "totp" }>(
      `${BASE}/login`,
      { method: "POST", body: { email, password } },
    ),
  verify: (challenge_token: string, code: string, use_recovery_code: boolean) =>
    request<Session | RecoverySetup>(`${BASE}/verify`, {
      method: "POST",
      body: { challenge_token, code, use_recovery_code },
    }),
  logout: () => request<void>(`${BASE}/logout`, { method: "POST" }),
  preview: (token: string) =>
    request<{
      email: string;
      display_name: string;
      account_exists: boolean;
    }>(`${BASE}/invite/preview`, { method: "POST", body: { token } }),
  inviteStart: (token: string, new_password: string) =>
    request<Setup>(`${BASE}/invite/start`, {
      method: "POST",
      body: { token, new_password },
    }),
  setupConfirm: (setup_token: string, code: string) =>
    request<ConfirmedSession>(`${BASE}/setup/confirm`, {
      method: "POST",
      body: { setup_token, code },
    }),
  security: () =>
    request<{
      enabled: boolean;
      enabled_at: string | null;
      recovery_codes_remaining: number;
    }>(`${BASE}/security`),
  recoveryCodes: (password: string, code: string) =>
    request<{ recovery_codes: string[] }>(`${BASE}/recovery-codes`, {
      method: "POST",
      body: { password, code },
    }),
  resetTotp: (password: string, code: string) =>
    request<Setup>(`${BASE}/reset-totp/start`, {
      method: "POST",
      body: { password, code },
    }),
  forgotPassword: (email: string, locale: "de" | "en") =>
    request<{ accepted: true }>(`${BASE}/forgot-password`, {
      method: "POST",
      body: { email, locale },
    }),
  resetPassword: (token: string, new_password: string) =>
    request<{ reset: true }>(`${BASE}/reset-password`, {
      method: "POST",
      body: { token, new_password },
    }),
  accounts: () =>
    request<{
      accounts: Array<{
        id: string;
        email: string;
        display_name: string;
        is_active: boolean;
        mfa_enabled: boolean;
        created_at: string;
      }>;
      invitations: Array<{
        id: string;
        email: string;
        display_name: string;
        created_at: string;
      }>;
    }>(`${BASE}/accounts`),
  inviteAdmin: (
    email: string,
    display_name: string,
    password: string,
    code: string,
  ) =>
    request<{
      id: string;
      email: string;
      display_name: string;
      activation_url: string;
    }>(`${BASE}/invitations`, {
      method: "POST",
      body: { email, display_name, password, code },
    }),
  setAdminActive: (
    id: string,
    is_active: boolean,
    password: string,
    code: string,
  ) =>
    request<unknown>(`${BASE}/accounts/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: { is_active, password, code },
    }),
  revokeInvitation: (id: string, password: string, code: string) =>
    request<void>(`${BASE}/invitations/${encodeURIComponent(id)}/revoke`, {
      method: "POST",
      body: { password, code },
    }),
};
